import {
  ActualityAiQueryContextFragment,
  BatchJobStatus,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { cn } from '../common/utils';
import { Button } from '../components/actions/Button';
import CodeEditor from '../components/editors/CodeEditor';
import * as Icon from '../components/icons';
import InboxZero from '../components/InboxZero';
import { Spacer } from '../components/layout/Spacer';
import MarkdownContent from '../components/markdown/MarkdownContent';
import TimeAgo from '../dashboard/TimeAgo';
import Callout from '../helpContext/Callout';
import { loggedInUser } from '../store/auth.state';
import AiAgentResponseChart from './AiAgentResponseChart';
import AiAgentThinking from './AiAgentThinking';
import { aiAgentMessageById } from './store/aiAgent.state';
import AiAgentResponseTable from './store/AiAgentResponseTable';

export type AiAgentMessageParams = {
  messageId: string;
};

interface AiAgentMessageProps {
  messageId: string;
  onRemoveClick: (messageId: string) => void;
}

const AiAgentMessage: React.FC<AiAgentMessageProps> = props => {
  const { t } = useTranslation('app');
  const message = useRecoilValue(aiAgentMessageById(props.messageId));
  const user = useRecoilValue(loggedInUser);

  const hasChart =
    !_.isNil(message.result?.visualizationType?.visualizationType) &&
    message.result?.visualizationType?.visualizationType !== 'table';

  // Add state for view mode
  const [activeViews, setActiveViews] = useState<
    ('table' | 'query' | 'chart' | 'analysis')[]
  >(() => {
    // Start with table view always active
    const initialViews: ('table' | 'query' | 'chart' | 'analysis')[] = [
      'table',
    ];

    // Add chart if available
    if (hasChart) {
      initialViews.push('chart');
    }

    // Add analysis if available
    if (
      message?.result?.agentResponses?.data_retrieval_expert?.resultAnalysis
    ) {
      initialViews.push('analysis');
    }

    // Query is never shown by default

    return initialViews;
  });

  const toggleView = (view: 'table' | 'query' | 'chart' | 'analysis') => {
    setActiveViews(current => {
      if (current.includes(view)) {
        return current.filter(v => v !== view);
      } else {
        return [...current, view];
      }
    });
  };

  const isViewActive = (view: 'table' | 'query' | 'chart' | 'analysis') =>
    activeViews.includes(view);

  const getTableHeight = () => {
    const data = message.result?.queryResult?.result || [];
    const rowCount = Math.min(data.length, 12); // Cap at 12 rows
    return rowCount * 28 + 100;
  };

  const sqlQuery = message.result?.sqlQuery;
  const clarification = message.result?.queryClarification;
  const isLoading =
    message.status === BatchJobStatus.CALCULATING ||
    message.status === BatchJobStatus.RESTARTED ||
    message.status === BatchJobStatus.CREATED;
  const hasError =
    message.status === BatchJobStatus.FAILED ||
    message.status === BatchJobStatus.TERMINATED;

  const hasTableResult = !_.isNil(message.result?.queryResult?.result);

  const hasAnalysis =
    !!message?.result?.agentResponses?.data_retrieval_expert?.resultAnalysis;

  const messageContext = message?.context as ActualityAiQueryContextFragment;

  return (
    <div className="group relative flex flex-col p-4">
      <div
        data-component="user-input-action-helper"
        className="flex items-center justify-end pb-1 pt-2 text-end text-xs opacity-0 transition-all duration-300 group-hover:opacity-100"
        // title={t`Created at`}
      >
        <div className="flex space-x-2">
          {/* Remove button (existing) */}
          <Button
            buttonSize="xs"
            isTransparent
            className={cn(
              'group',
              'rounded',
              'flex flex-col items-center justify-center',
              'bg-app-panel/20',
              'hover:bg-menu-hover hover:text-menu-active-text',
              'p-0.5',
              'rounded-full',
            )}
            // buttonType="secondary"
            onPress={() => props.onRemoveClick(props.messageId)}
          >
            <span className="text-xs ltr:mr-2 rtl:ml-2">{t`Remove`}</span>
            <Icon.Close className={cn('h-3 w-3', 'mx-1', 'fill-current')} />
          </Button>
        </div>

        <TimeAgo
          timeDate={new Date(message.startedAt)}
          updatedDate={new Date(message.updatedAt)}
          createdDate={new Date(message.createdAt)}
          showDuration={true}
          durationType="short"
        />
      </div>

      <div data-component="user-input" className="flex justify-end">
        <div className="bg-brand text-menu-active-text inline-flex max-w-[80%] rounded-2xl rounded-tr-none px-4 py-3 shadow-sm transition-all duration-200">
          <div
            data-component="user-avatar"
            className="bg-sidebar-header text-brand flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full p-0.5 text-xs ltr:mr-2 rtl:ml-2"
          >
            {`${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`}
          </div>
          <div className="inline select-text">{message.query}</div>
        </div>
      </div>

      <div
        data-component="user-input-action-helper"
        className="flex items-center justify-end pt-2 text-end text-xs opacity-0 transition-all duration-300 group-hover:opacity-100"
      >
        <div className="flex space-x-2">
          {/* Chart button */}
          <Button
            buttonSize="xs"
            isTransparent
            className={cn(
              'group',
              'rounded',
              'flex flex-col items-center justify-center',
              'p-0.5',
              'rounded-full',
              isViewActive('chart')
                ? 'bg-brand/10 text-brand border-brand/50'
                : 'bg-app-panel/20 hover:bg-menu-hover hover:text-menu-active-text',
            )}
            onPress={() => toggleView('chart')}
          >
            <span className="text-xs ltr:mr-2 rtl:ml-2">
              {isViewActive('chart') ? t`Hide Chart` : t`Show Chart`}
            </span>
          </Button>

          {/* Table button */}
          <Button
            buttonSize="xs"
            isTransparent
            className={cn(
              'group',
              'rounded',
              'flex flex-col items-center justify-center',
              'p-0.5',
              'rounded-full',
              isViewActive('table')
                ? 'bg-brand/10 text-brand border-brand/50'
                : 'bg-app-panel/20 hover:bg-menu-hover hover:text-menu-active-text',
            )}
            onPress={() => toggleView('table')}
          >
            <span className="text-xs ltr:mr-2 rtl:ml-2">
              {isViewActive('table') ? t`Hide Table` : t`Show Table`}
            </span>
          </Button>
        </div>
      </div>

      {/* {hasError && <ErrorIndicator message={t`Error`} />} */}

      {isLoading ? (
        <div data-component="ai-output" className="select-text">
          <div className="px-4 py-3 shadow-sm">
            <Callout
              panelMode
              type="suggestion"
              className="!bg-app-panel/20 mt-2"
            >
              <div className="flex items-center gap-2">
                <AiAgentThinking />
              </div>
            </Callout>
          </div>
        </div>
      ) : (
        <>
          <div data-component="ai-output" className="select-text">
            <div className="px-4 py-3 shadow-sm">
              <Callout
                panelMode
                type="suggestion"
                title={
                  hasError
                    ? t`Hmm..., something went wrong, please try asking again.`
                    : null
                }
                className="!bg-app-panel/20 mt-2"
              >
                <MarkdownContent
                  content={message?.result?.modelResponse?.message}
                  className="mb-4"
                />

                {clarification && (
                  <MarkdownContent content={clarification} className="mb-4" />
                )}

                {(sqlQuery || hasTableResult) && (
                  <>
                    {/* <div className="text-menu-text bg-app-panel/70 mb-2 mt-2 flex gap-2 rounded-full p-1 text-sm shadow-sm">
                      {[
                        { id: 'table' as const, label: t`Data`, show: true },
                        {
                          id: 'chart' as const,
                          label: t`Chart`,
                          show: hasChart,
                        },
                        {
                          id: 'query' as const,
                          label: t`Query`,
                          show: sqlQuery,
                        },
                      ].map(
                        ({ id, label, show }) =>
                          show && (
                            <button
                              key={id}
                              onClick={() => toggleView(id)}
                              className={cn(
                                'flex-1',
                                'rounded-xl px-3 py-1',
                                // 'border-menu-text/50 border',
                                'hover:text-menu-text-hover hover:border-menu-text/75',
                                'transition-colors duration-200',
                                isViewActive(id)
                                  ? 'text-brand border-brand/50 bg-brand/10'
                                  : 'text-menu-text/75',
                              )}
                            >
                              {isViewActive(id) ? 'Hide' : 'Show'} {label}
                            </button>
                          ),
                      )}
                    </div> */}

                    {isViewActive('chart') && hasChart && (
                      // <AiAgentCharts
                      //   data={queryResult?.result || []}
                      //   columns={queryResult?.columns || []}
                      //   isABCVelocityDistribution={isABCVelocityDistribution()}
                      // />
                      <AiAgentResponseChart
                        messageId={props.messageId}
                        visualisationType={message.result.visualizationType}
                        queryResponse={message.result?.queryResult}
                      />
                    )}

                    {message.result.modelResponse.suggestion && (
                      <Callout
                        panelMode
                        type="suggestion"
                        title={t`Suggestion`}
                        className="mt-2 whitespace-pre"
                      >
                        {message.result.modelResponse.suggestion}
                      </Callout>
                    )}

                    <div className="py-1 text-xs">
                      {messageContext?.from && messageContext?.to && (
                        <div className={cn('flex items-center gap-2')}>
                          <span>{t`Data range:`}</span>
                          <span className="text-menu-text/50">
                            {formatDateRange(
                              messageContext?.from,
                              messageContext?.to,
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {isViewActive('table') &&
                      (hasTableResult ? (
                        <div
                          className="group flex flex-1 flex-col"
                          style={{ height: `${getTableHeight()}px` }}
                        >
                          <AiAgentResponseTable
                            messageId={message?.id}
                            queryResponse={message.result?.queryResult}
                          />
                        </div>
                      ) : (
                        <InboxZero message={`No Data found`} className="mt-2" />
                      ))}

                    {isViewActive('analysis') && (
                      <>
                        <MarkdownContent
                          content={
                            message?.result?.agentResponses
                              ?.data_retrieval_expert?.resultAnalysis
                          }
                          className="mb-4"
                        >
                          <span className="text-menu-active">{t`AI Analysis:`}</span>
                        </MarkdownContent>
                      </>
                    )}

                    <div
                      data-component="user-input-footer-helper"
                      className="flex items-center justify-end gap-2 py-2 text-end text-xs opacity-0 transition-all duration-300 group-hover:opacity-100"
                    >
                      {/* Analysis button */}
                      {message?.result?.agentResponses?.data_retrieval_expert
                        ?.resultAnalysis && (
                        <Button
                          buttonSize="xs"
                          isTransparent
                          className={cn(
                            'group',
                            'rounded',
                            'flex flex-col items-center justify-center',
                            'p-0.5',
                            'rounded-full',
                            isViewActive('analysis')
                              ? 'bg-brand/10 text-brand border-brand/50'
                              : 'bg-app-panel/20 hover:bg-menu-hover hover:text-menu-active-text',
                          )}
                          onPress={() => toggleView('analysis')}
                        >
                          <Icon.HelpBot
                            className={cn('h-4 w-4', 'mx-1', 'fill-current')}
                          />

                          <span className="text-xs ltr:mr-2 rtl:ml-2">
                            {isViewActive('analysis')
                              ? t`Hide Analysis`
                              : t`Show Analysis`}
                          </span>
                        </Button>
                      )}
                      <Spacer flexspace />
                      <Button
                        buttonSize="xs"
                        isTransparent
                        className={cn(
                          'group',
                          'rounded',
                          'flex flex-col items-center justify-center',
                          'p-0.5',
                          'rounded-full',
                          isViewActive('query')
                            ? 'bg-brand/5 text-brand border-brand/50'
                            : 'bg-app-panel/20 hover:bg-menu-hover hover:text-menu-active-text',
                        )}
                        onPress={() => toggleView('query')}
                      >
                        <span className="text-xs ltr:mr-2 rtl:ml-2">
                          {isViewActive('query')
                            ? t`Hide Query`
                            : t`Show Query`}
                        </span>
                      </Button>
                    </div>
                    {isViewActive('query') && sqlQuery && (
                      <div
                        data-component="CodeSnippet"
                        className={cn(
                          'font-mono text-xs',
                          'bg-app-panel-dark/60',
                          'text-menu-text/60',
                          'text-left',
                          'whitespace-pre-wrap',
                          'select-text',
                          'p-0.5',
                          'w-full',
                          'rounded-b-sm',
                          'mb-2',
                          'min-h-[24px]',
                          'overflow-hidden',
                          'flex flex-col',
                        )}
                      >
                        {/* {message?.response?.modelResponse?.thinking && (
                          <MarkdownContent
                            content={message?.response?.modelResponse?.thinking}
                            className="!text-xxs mb-4 p-3"
                          />
                        )} */}
                        <CodeEditor
                          value={sqlQuery}
                          language={'trinosql'}
                          height={`${
                            Math.max(
                              sqlQuery
                                ?.split('\n')
                                .map(line => Math.ceil(line.length / 80))
                                .reduce((a, b) => a + b, 0) || 1,
                            ) *
                              28 +
                            16
                          }px`}
                          minHeight="20px"
                          className="flex-1"
                          editorOptions={{
                            readOnly: true,
                            wordWrap: 'on',
                            lineNumbers: 'off',
                            minimap: { enabled: false },
                            scrollbar: {
                              vertical: 'hidden',
                              horizontal: 'hidden',
                            },
                            padding: { top: 8, bottom: 8 },
                          }}
                        />
                        {clarification && (
                          <div className="">
                            <Callout
                              panelMode
                              type="suggestion"
                              title={t`Clarification`}
                              className="mt-2"
                            >
                              {clarification}
                            </Callout>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Callout>
              {/* end of chat */}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

function formatDateRange(fromStr: string, toStr: string) {
  const from = new Date(fromStr);
  const to = new Date(toStr);

  const sameDay =
    from.getDate() === to.getDate() &&
    from.getMonth() === to.getMonth() &&
    from.getFullYear() === to.getFullYear();

  const sameYear = from.getFullYear() === to.getFullYear();

  if (sameDay) {
    // Same day: "Feb 13, 1:20 PM - 2:20 PM"
    return `${from.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}, ${from.toLocaleString([], {
      hour: '2-digit',
      minute: '2-digit',
    })} - ${to.toLocaleString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  } else if (sameYear) {
    // Same year: "Feb 13 - Mar 15, 2025"
    return `${from.toLocaleString([], {
      month: 'short',
      day: 'numeric',
    })} - ${to.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  } else {
    // Different years: "Feb 13, 2025 - Mar 15, 2026"
    return `${from.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })} - ${to.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })}`;
  }
}

export default AiAgentMessage;
